/**
 * Sugestões (v1, beta — exceção do PO em 18/09/2026).
 *
 * Os links que o grupo guardou sobre a viagem (vídeos do YouTube/TikTok/
 * Instagram, páginas), mais recente primeiro, com categoria, quem mandou e
 * quando. Tocar abre o link fora do app. Apaga quem criou ou quem administra.
 * Sem curtir, sem comentários (v1 enxuta). Tela interna: "+" no cabeçalho.
 */
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useTrip } from '../../trip/TripContext';
import { Icon } from '../../ui/icons';
import { EmptyState, ErrorState, Skeleton } from '../../ui/primitives';
import { CATEGORIA_ROTULO, PROVEDOR_ROTULO, type Sugestao, useApagarSugestao, useSugestoes } from './api';
import { NovaSugestaoSheet } from './NovaSugestaoSheet';
import { VinculoDaSugestao } from './VinculoDaSugestao';

function quando(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function Cartao({ s, onApagar, online }: { s: Sugestao; onApagar: (id: string) => void; online: boolean }) {
  const [confirmando, setConfirmando] = useState(false);
  return (
    <article className="sug" data-testid="sugestao">
      <a className="sug__link" href={s.url} target="_blank" rel="noopener noreferrer" aria-label={`Abrir: ${s.title ?? s.host}`}>
        {s.thumbnailUrl ? (
          <img className="sug__thumb" src={s.thumbnailUrl} alt="" loading="lazy" />
        ) : (
          <span className="sug__thumb sug__thumb--vazio" aria-hidden="true">
            <Icon name="externo" size={22} />
          </span>
        )}
        <span className="sug__texto">
          <span className="sug__origem">
            {PROVEDOR_ROTULO[s.provider]} · {s.host}
          </span>
          <span className="sug__titulo">{s.title ?? s.host}</span>
          {s.authorName && <span className="sug__autor">{s.authorName}</span>}
        </span>
      </a>
      {s.comment && <p className="sug__comentario-texto">{s.comment}</p>}
      <div className="sug__rodape">
        <span className="pick pick--mini">{CATEGORIA_ROTULO[s.category]}</span>
        <span className="sug__quem">
          {s.createdBy.me ? 'Você' : s.createdBy.name} · {quando(s.createdAt)}
        </span>
        {s.canDelete &&
          (confirmando ? (
            <span className="sug__confirma">
              <button type="button" className="nv-btn nv-btn--chip" onClick={() => setConfirmando(false)}>
                Manter
              </button>
              <button type="button" className="nv-btn nv-btn--chip nv-btn--danger" onClick={() => onApagar(s.id)} data-testid="sug-apagar-confirma">
                Apagar
              </button>
            </span>
          ) : (
            <button type="button" className="sug__apagar" aria-label="Apagar sugestão" onClick={() => setConfirmando(true)} data-testid="sug-apagar">
              <Icon name="excluir" size={16} />
            </button>
          ))}
      </div>
      <VinculoDaSugestao s={s} online={online} />
    </article>
  );
}

export function SugestoesScreen() {
  const { tripId } = useTrip();
  const [busca, setBusca] = useSearchParams();
  const [nova, setNova] = useState(busca.get('novo') === '1');
  const lista = useSugestoes(tripId);
  const apagar = useApagarSugestao(tripId);
  // Vincular/decidir fala com o servidor na hora (Sugestões não tem outbox).
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const mudou = () => setOnline(navigator.onLine);
    window.addEventListener('online', mudou);
    window.addEventListener('offline', mudou);
    return () => {
      window.removeEventListener('online', mudou);
      window.removeEventListener('offline', mudou);
    };
  }, []);

  const fecharNova = () => {
    setNova(false);
    if (busca.get('novo')) setBusca({}, { replace: true });
  };

  const itens = lista.data?.suggestions ?? [];

  return (
    <section className="screen sug-tela">
      <header className="mem__cabecalho">
        <div>
          <h1 className="mem__titulo">Sugestões</h1>
        </div>
        <button type="button" className="mem__mais" onClick={() => setNova(true)} aria-label="Nova sugestão" data-testid="sug-nova">
          <Icon name="criar" size={22} />
        </button>
      </header>

      {lista.isLoading && <Skeleton height={96} count={3} />}
      {lista.isError && <ErrorState title="Não foi possível carregar" description="Verifique a conexão e tente de novo." onRetry={() => void lista.refetch()} />}
      {lista.isSuccess && itens.length === 0 && (
        <EmptyState
          ilustracao="sugestao"
          title="Nenhuma sugestão ainda"
          description="Viu um vídeo ou uma página sobre o destino? Cole o link aqui — o grupo inteiro vê."
          action={{ label: 'Nova sugestão', onClick: () => setNova(true) }}
        />
      )}
      {itens.map((s) => (
        <Cartao key={s.id} s={s} onApagar={(id) => void apagar.mutateAsync(id)} online={online} />
      ))}

      {nova && <NovaSugestaoSheet onFechar={fecharNova} />}
    </section>
  );
}
